import { Logger } from 'Common/appshell_types';
import SilentLogger from 'Client/logger/silentLogger';

export type SegmentDestination = {
    name: string;
    creationName: string;
    description: string;
    website: string;
    category: string;
};

const ONE_TRUST_SEGMENT_MAPPING: Record<string, string[]> = {
    /* Strictly Necessary Cookies */
    C0001: [],

    /* Performance Cookies */
    C0002: [
        'A/B Testing',
        'Analytics',
        'Attribution',
        'Email',
        'Email Marketing',
        'Enrichment',
        'Feature Flagging',
        'Heatmaps & Recordings',
        'Raw Data',
        'Realtime Dashboards',
        'Referrals',
        'Surveys',
        'Video',
    ],

    /* Functional Cookies */
    C0003: [
        'CRM',
        'Customer Success',
        'Deep Linking',
        'Helpdesk',
        'Livechat',
        'Marketing Automation',
        'Performance Monitoring',
        'Personalization',
        'SMS & Push Notifications',
        'Security & Fraud',
    ],

    /* Targeting Cookies */
    C0004: ['Advertising', 'Tag Managers'],
};

interface SegmentSDKEventPayload {
    obj: {
        properties?: {
            journeyID?: string;
            isBot?: boolean;
            isE2E?: boolean;
        };
    };
}

type SegmentSDKMiddleware = (props: {
    payload: SegmentSDKEventPayload;
    next: (x: SegmentSDKEventPayload) => void;
}) => void;

export interface SegmentTrackingConfig {
    journeyID: string;
    segmentIntegrations: SegmentDestination[];
    isBot: boolean;
    isE2E: boolean;
}
export interface SegmentSDK {
    track: (eventName: string, props?: Object) => void;
    identify: (id: string | Object, props?: Object) => void;
    page: (category?: string | object, name?: string | object, properties?: object | null) => void;
    load: (key: string, props?: Object) => void;
    reset: () => void;
    setAnonymousId: (id: string) => void;
    initialized: boolean;
    addSourceMiddleware: (x: SegmentSDKMiddleware) => void;
}

export interface SegmentTrackerInterface {
    track: (eventName: string, props?: Object | undefined) => void;
    page: (category?: string | object, name?: string | object, properties?: object | null) => void;
    identify: (id: string, props?: Object | undefined) => void;
    reset: () => void;
    onConsentReceived: (props: {
        allGroups: string[];
        journeyID?: string;
        segmentIntegrations?: SegmentDestination[];
    }) => void;
}
export class SegmentTracker implements SegmentTrackerInterface {
    private writeKey: string;

    private segmentTrackingConfig: SegmentTrackingConfig;

    private isDummy = false;

    private logger: Logger;

    public constructor({
        writeKey,
        enabled,
        logger = SilentLogger.createInstance(),
        segmentTrackingConfig,
    }: {
        writeKey?: string;
        enabled?: boolean;
        logger?: Logger;
        segmentTrackingConfig?: SegmentTrackingConfig;
    }) {
        this.logger = logger;
        this.writeKey = writeKey || '';

        if (typeof window === 'undefined' || !enabled) {
            this.isDummy = true;
        } else if (!writeKey) {
            logger.error('[SEGMENT] missing writeKey cannot initialize instance');
            this.isDummy = true;
        } else if (typeof window.analytics === 'undefined') {
            logger.error('[SEGMENT] analytics not found in window object, was script loaded?');
            this.isDummy = true;
        } else if (typeof segmentTrackingConfig === 'undefined') {
            logger.error('[SEGMENT] segmentTrackingConfig not found');
            this.isDummy = true;
        }

        if (this.isDummy) {
            return this;
        }

        this.segmentTrackingConfig = segmentTrackingConfig as SegmentTrackingConfig;
        this.initCustomUserTraitMiddleware();
        return this;
    }

    public track(eventName: string, props?: Object | undefined): void {
        if (!this.isDummy) {
            window.analytics.track(eventName, props);
        }
    }

    public identify(id: string, props?: Object | undefined): void {
        if (!this.isDummy) {
            window.analytics.identify(id, props);
        }
    }

    public page(name?: string, category?: string, properties?: object | null): void {
        if (!this.isDummy) {
            window.analytics.page(category, name, properties);
        }
    }

    public reset(): void {
        if (!this.isDummy) {
            window.analytics.reset();
        }
    }

    /**
     * Returns a list of Segment Destinations / Integrations that the user has consented to (by consenting to OneTrust categories)
     */
    private getConsentedIntegrations = (enabledIntegrations: SegmentDestination[], oneTrustGroupIds: string[]) => {
        /* Get consented segment categories. */
        const segmentCategories = oneTrustGroupIds
            .map(oneTrustGroupId => ONE_TRUST_SEGMENT_MAPPING[oneTrustGroupId])
            .filter(value => value)
            .reduce((a, b) => a.concat(b), []);

        /* Filter enabled integrations by consented segment categories. */
        const consentedIntegrations = enabledIntegrations.filter(
            enabledIntegration => segmentCategories.indexOf(enabledIntegration.category) > -1
        );

        return consentedIntegrations;
    };

    private initCustomUserTraitMiddleware() {
        if (this.isDummy) {
            return;
        }
        const customUserTraitMiddleware: SegmentSDKMiddleware = ({ payload, next }) => {
            // as per segment docs, not deconstructing payload.obj
            /* eslint-disable no-param-reassign */
            payload.obj.properties = {
                journeyID: this.segmentTrackingConfig.journeyID,
                isBot: this.segmentTrackingConfig.isBot,
                isE2E: this.segmentTrackingConfig.isE2E,
                ...payload.obj.properties,
            };
            /* eslint-enable no-param-reassign */
            next(payload);
        };
        window.analytics.addSourceMiddleware(customUserTraitMiddleware);
    }

    public onConsentReceived = ({ allGroups }: { allGroups: string[] }) => {
        if (this.isDummy) {
            return;
        }
        const { segmentIntegrations } = this.segmentTrackingConfig;
        /* Retrieve segment specific consented integrations. */
        const consentedIntegrations = this.getConsentedIntegrations(segmentIntegrations || [], allGroups);

        const destinationPreferences: { [key: string]: boolean } = {};
        consentedIntegrations.forEach(consentedIntegration => {
            destinationPreferences[consentedIntegration.creationName] = true;
        });

        if (Object.keys(destinationPreferences).length > 0 && window.analytics) {
            if (!window.analytics.initialized) {
                window.analytics.load(this.writeKey, {
                    integrations: { All: false, 'Segment.io': true, ...destinationPreferences },
                });
            } else {
                this.logger.error('[SEGMENT] tracker already initialized when consent received');
            }
        }
    };
}
